<script>
import {useUserStore} from "@/stores/Setting/User.js";
import useValidation from "@/use/Validation.js";
import useSharedData from "@/use/SharedData.js";
import useTools from "@/use/Tools.js";
import useSharedMethod from "@/use/SharedMethod.js";
import {computed, onMounted, ref} from "vue";
import {useSecurityStore} from "@/stores/Security";
import axios from "axios";

export default {
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        deleteAction: Boolean,
        viewAction: Boolean,
    },
    setup(props) {
        const readonly = computed(() => props.viewAction || props.deleteAction);
        const {mxRules} = useValidation();
        const userStore = useUserStore();
        const securityStore = useSecurityStore();
        const {dialog, loading, overlay} = useSharedData();
        const {$refs, $route} = useTools();
        const {getBack, logout} = useSharedMethod();


        const user = ref({});
        const motDePasse = ref(null);
        const confirmMotDePasse = ref(null);

        function findById(id) {
            loading.value = true;
            axios.get(`/users/${id}`).then(response => {
                user.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }

        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                user.value.motDePasse = motDePasse.value;
                axios.post('/users', user.value).then(response => {
                    userStore.addUser(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;

                });
            }
        }

        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                user.value.motDePasse = motDePasse.value;
                axios.put('/users', user.value).then(response => {
                    userStore.updateUser(response.data);
                    if (securityStore.userContext.id === $route.params.id) {
                        logout();
                    } else {
                        getBack();
                    }
                }).finally(() => {
                    loading.value = false;

                });
            }
        }

        function remove() {
            loading.value = true;
            axios.delete(`/users/${user.value.id}`).then(() => {
                userStore.deleteUser(user.value);
                getBack();

            }).finally(() => {
                loading.value = false;
            });
        }

        onMounted(() => {
            if (!props.addAction)
                findById($route.params.id);
        });

        return {
            readonly,
            mxRules,
            userStore,
            dialog,
            loading,
            overlay,
            user,
            motDePasse,
            confirmMotDePasse,
            save,
            update,
            remove,
            getBack,
        }
    }
};
</script>

<template>
    <v-dialog
        transition="dialog-bottom-transition"
        v-model="dialog"
        persistent
        width="800"
        scrollable
    >
        <v-card>
            <w-dialog-toolbar
                :add-action="addAction"
                :update-action="updateAction"
                :delete-action="deleteAction"
                :view-action="viewAction"
            />
            <w-overlay
                :overlay="overlay"
                :loading="loading"
                :delete-action="deleteAction"
                @remove="remove"
                @cancel="overlay = false"
            ></w-overlay>
            <v-card-text>
                <v-form ref="form" lazy-validation>
                    <v-container fluid>
                        <v-row>
                            <v-col cols="6">
                                <strong>{{ $t('common.username') }} *</strong>
                                <w-text-field
                                    v-model="user.username"
                                    :readonly="!addAction"
                                    :rules="[mxRules.required]"
                                />
                            </v-col>
                            <v-col cols="6">
                                <strong>{{ $t('common.email') }} *</strong>
                                <w-text-field
                                    v-model="user.email"
                                    :readonly="readonly"
                                    :rules="[mxRules.required, mxRules.email]"
                                />
                            </v-col>
                            <v-col cols="6">
                                <strong>{{ $t('common.lastname') }} *</strong>
                                <w-text-field
                                    v-model="user.lastName"
                                    :readonly="readonly"
                                    :rules="[mxRules.required]"
                                />
                            </v-col>
                            <v-col cols="6">
                                <strong>{{ $t('common.firstname') }} *</strong>
                                <w-text-field
                                    v-model="user.firstName"
                                    :readonly="readonly"
                                    :rules="[mxRules.required]"
                                />
                            </v-col>
                            <v-col cols="6">
                                <strong>{{ $t('module.role') }} *</strong>
                                <str-role-autocomplete
                                    v-model="user.roleId"
                                    :readonly="readonly"
                                    :rules="[mxRules.required]"
                                />
                            </v-col>
                            <v-col cols="6">
                                <strong>{{ $t('module.worker') }} *</strong>
                                <str-worker-autocomplete
                                    v-model="user.workerId"
                                    :readonly="readonly"
                                />
                            </v-col>
                            <v-col cols="6">
                                <w-label label="Mot de Passe" :required="addAction"/>
                                <w-password-text-field
                                    v-model="motDePasse"
                                    :readonly="readonly"
                                    :rules="addAction ? [mxRules.required] : []"
                                />
                            </v-col>
                            <v-col cols="6">
                                <w-label label="Confirmer Mot de Passe" :required="addAction"/>
                                <w-password-text-field
                                    v-model="confirmMotDePasse"
                                    :readonly="readonly"
                                    :rules="motDePasse ? [mxRules.required, mxRules.passwordMatch(motDePasse)] : []"
                                />
                            </v-col>
                            <v-col cols="12">
                                <v-checkbox
                                    v-model="user.validator"
                                    label="Validateur"
                                    :readonly="readonly"
                                    color="teal"
                                />
                            </v-col>
                        </v-row>
                    </v-container>
                </v-form>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-spacer/>
                <w-btn-close @press="getBack"/>
                <w-btn-add
                    :show="addAction"
                    :loading="loading"
                    @press="save"
                />
                <w-btn-update
                    :show="updateAction"
                    :loading="loading"
                    @press="update"
                />
                <w-btn-delete
                    :show="deleteAction"
                    :loading="loading"
                    @press="overlay = true"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style scoped>
</style>
